import { format } from "date-fns";
import type { EmploymentHistoryItem } from "../../types/candidate";

/**
 * Normalise a URL to use HTTPS. Prepends "https://" if no protocol is present.
 */
export function ensureHttps(url: string): string {
  if (url.startsWith("https://")) return url;
  if (url.startsWith("http://")) return "https://" + url.slice(7);
  return "https://" + url;
}

/**
 * Format an ISO date string to "MMM yyyy" (e.g. "Jan 2024").
 * Returns the raw string if the date is invalid.
 */
export function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return format(date, "MMM yyyy");
}

/**
 * Initials from a full name — one letter per word, all words
 * (e.g. "Doan Hoang Hiep" -> "DHH"). Returns "?" if no valid
 * characters are found.
 */
export function getInitials(name: string | null | undefined): string {
  if (!name) return "?";
  const initials = name
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
  return initials || "?";
}

/**
 * Return a Tailwind text-color class based on the confidence score value.
 * - >= 80: green-600
 * - >= 50: orange-500
 * - < 50: error (red)
 */
export function getScoreColor(score: number): string {
  const rounded = Math.round(score);
  if (rounded >= 80) return "text-green-600";
  if (rounded >= 50) return "text-orange-500";
  return "text-error";
}

/**
 * Sort employment history items: current jobs first, then by start_date descending.
 * Handles missing or invalid dates by pushing them to the end.
 */
export function sortEmploymentByDate(
  items: EmploymentHistoryItem[]
): EmploymentHistoryItem[] {
  return [...items].sort((a, b) => {
    // Current jobs always come first
    if (a.is_current && !b.is_current) return -1;
    if (!a.is_current && b.is_current) return 1;

    // Sort by start_date descending (most recent first)
    const dateA = new Date(a.start_date).getTime();
    const dateB = new Date(b.start_date).getTime();
    const validA = !isNaN(dateA);
    const validB = !isNaN(dateB);

    if (validA && validB) return dateB - dateA;
    if (validA && !validB) return -1;
    if (!validA && validB) return 1;
    return 0;
  });
}

/** "full-time" → "Full-time" (employment_history_items.employment_type). */
export function employmentTypeLabel(type?: string | null): string | null {
  if (!type) return null;
  const t = type.trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : null;
}

/** "8 yrs exp" (v3 column copy). */
export function yearsExpLabel(years?: number | null): string | null {
  if (years === null || years === undefined) return null;
  return `${years} ${years === 1 ? "yr" : "yrs"} exp`;
}
